import React, { useRef, useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Box,
  TextField,
  Button,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  Palette as PaletteIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { ApplicationThemeSettings, PermissionUtils } from './types';

const DEFAULT_PRIMARY = '#1976d2';
const DEFAULT_SECONDARY = '#dc004e';

const toPickerColor = (value: string, fallback: string) => {
  const hex = (value || '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
    return hex;
  }
  if (/^#[0-9A-Fa-f]{3}$/.test(hex)) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return fallback;
};

interface ThemeSettingsProps {
  themeSettings: ApplicationThemeSettings;
  setThemeSettings: React.Dispatch<React.SetStateAction<ApplicationThemeSettings>>;
  onSave: () => Promise<boolean | void>;
  permissionUtils: PermissionUtils;
}

const ThemeSettings: React.FC<ThemeSettingsProps> = ({
  themeSettings,
  setThemeSettings,
  onSave,
  permissionUtils
}) => {
  const { canEditSection } = permissionUtils;
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [colorSnapshot, setColorSnapshot] = useState<ApplicationThemeSettings | null>(null);
  const primaryPickerRef = useRef<HTMLInputElement>(null);
  const secondaryPickerRef = useRef<HTMLInputElement>(null);

  const canEdit = canEditSection('theme');
  const fieldsDisabled = !canEdit || !isEditing;
  const primaryPreview = toPickerColor(themeSettings.primaryColor, DEFAULT_PRIMARY);
  const secondaryPreview = toPickerColor(themeSettings.secondaryColor, DEFAULT_SECONDARY);

  const startEdit = () => {
    setColorSnapshot(themeSettings);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    if (colorSnapshot) {
      setThemeSettings(colorSnapshot);
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const saved = await onSave();
      if (saved !== false) {
        setIsEditing(false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleThemeChange = (field: keyof ApplicationThemeSettings) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setThemeSettings(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PaletteIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Application Theme</Typography>
          {!canEdit && (
            <Chip
              label="Read Only"
              color="default"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
          {canEdit && (
            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              {isEditing ? (
                <>
                  <Tooltip title={isSaving ? 'Saving...' : 'Save'}>
                    <span>
                      <IconButton
                        color="primary"
                        onClick={handleSave}
                        disabled={isSaving}
                        size="small"
                        aria-label="Save application theme"
                      >
                        <SaveIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <span>
                      <IconButton
                        onClick={cancelEdit}
                        disabled={isSaving}
                        size="small"
                        aria-label="Cancel application theme edit"
                      >
                        <CancelIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title="Edit">
                  <IconButton
                    color="primary"
                    onClick={startEdit}
                    size="small"
                    aria-label="Edit application theme"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Primary Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  onClick={() => !fieldsDisabled && primaryPickerRef.current?.click()}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: primaryPreview,
                    border: '2px solid',
                    borderColor: primaryPreview,
                    borderRadius: 1,
                    cursor: fieldsDisabled ? 'default' : 'pointer',
                    flexShrink: 0,
                    boxShadow: `0 0 0 1px ${primaryPreview}33`,
                  }}
                />
                <TextField
                  fullWidth
                  label="Primary Color Code"
                  value={themeSettings.primaryColor}
                  onChange={handleThemeChange('primaryColor')}
                  disabled={fieldsDisabled}
                  variant="outlined"
                  size="small"
                  placeholder={DEFAULT_PRIMARY}
                  InputProps={{
                    startAdornment: (
                      <input
                        ref={primaryPickerRef}
                        type="color"
                        value={primaryPreview}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, primaryColor: e.target.value }))}
                        disabled={fieldsDisabled}
                        style={{
                          width: 20,
                          height: 20,
                          border: 'none',
                          borderRadius: '4px',
                          marginRight: '8px',
                          cursor: fieldsDisabled ? 'default' : 'pointer',
                          background: 'transparent',
                        }}
                      />
                    ),
                  }}
                />
              </Box>
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Secondary Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  onClick={() => !fieldsDisabled && secondaryPickerRef.current?.click()}
                  sx={{
                    width: 40,
                    height: 40,
                    backgroundColor: secondaryPreview,
                    border: '2px solid',
                    borderColor: secondaryPreview,
                    borderRadius: 1,
                    cursor: fieldsDisabled ? 'default' : 'pointer',
                    flexShrink: 0,
                    boxShadow: `0 0 0 1px ${secondaryPreview}33`,
                  }}
                />
                <TextField
                  fullWidth
                  label="Secondary Color Code"
                  value={themeSettings.secondaryColor}
                  onChange={handleThemeChange('secondaryColor')}
                  disabled={fieldsDisabled}
                  variant="outlined"
                  size="small"
                  placeholder={DEFAULT_SECONDARY}
                  InputProps={{
                    startAdornment: (
                      <input
                        ref={secondaryPickerRef}
                        type="color"
                        value={secondaryPreview}
                        onChange={(e) => setThemeSettings(prev => ({ ...prev, secondaryColor: e.target.value }))}
                        disabled={fieldsDisabled}
                        style={{
                          width: 20,
                          height: 20,
                          border: 'none',
                          borderRadius: '4px',
                          marginRight: '8px',
                          cursor: fieldsDisabled ? 'default' : 'pointer',
                          background: 'transparent',
                        }}
                      />
                    ),
                  }}
                />
              </Box>
            </Box>
          </Grid2>

          <Grid2 size={{ xs: 12 }}>
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Color Preview
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: primaryPreview,
                    '&:hover': { backgroundColor: primaryPreview },
                  }}
                >
                  Primary Button
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: secondaryPreview,
                    '&:hover': { backgroundColor: secondaryPreview },
                  }}
                >
                  Secondary Button
                </Button>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 160,
                    height: 36,
                    borderRadius: 1,
                    background: `linear-gradient(90deg, ${primaryPreview} 0%, ${secondaryPreview} 100%)`,
                  }}
                />
              </Box>
            </Box>
          </Grid2>
        </Grid2>
      </CardContent>
    </Card>
  );
};

export default ThemeSettings;
